<template>
  <div ref="sectionRef" class="relative overflow-hidden pt-12 lg:pt-30">
    <div
      class="text-center"
      @mouseover="
        emitterEmit('hotspotEnter', {
          label: 'Show next'
        })
      "
      @mouseleave="emitterEmit('hotspotLeave')"
    >
      <div ref="textRef" class="relative cursor-none">
        <NuxtLink :to="nextRecord.slug" class="inline-flex cursor-none flex-col items-center">
          <span class="text-sm uppercase text-cream">Up next</span>
          <Heading :size="2" :is-semantic="false" class="font-light text-yellow">{{
            nextRecord.title
          }}</Heading>
        </NuxtLink>
      </div>
      <NuxtLink ref="imageRef" :to="nextRecord.slug" class="cursor-none">
        <Image
          v-if="nextRecord.video?.thumbnail"
          class="aspect-[32/9] w-full object-cover object-top"
          loading="lazy"
          :src="
            nextRecord.video?.thumbnail?.responsiveImage
              ? nextRecord.video?.thumbnail?.responsiveImage?.src
              : nextRecord.video?.thumbnail?.url
          "
          :srcset="
            nextRecord.video?.thumbnail?.responsiveImage
              ? nextRecord.video?.thumbnail?.responsiveImage?.webpSrcSet
              : undefined
          "
          :width="
            nextRecord.video?.thumbnail?.responsiveImage
              ? nextRecord.video?.thumbnail?.responsiveImage?.width
              : nextRecord.video?.thumbnail?.width
          "
          :height="
            nextRecord.video?.thumbnail?.responsiveImage
              ? nextRecord.video?.thumbnail?.responsiveImage?.height
              : nextRecord.video?.thumbnail?.height
          "
          :alt="nextRecord.video?.thumbnail?.alt || ''"
        />
      </NuxtLink>
    </div>
    <div
      class="pointer-events-none absolute bottom-4 left-1/2 z-2 flex h-10 -translate-x-1/2 items-center gap-4 whitespace-nowrap rounded-full bg-black/60 px-4 uppercase text-yellow backdrop-blur-sm lg:hidden"
    >
      Show next
    </div>
  </div>
</template>

<script setup lang="ts">
import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'

interface ProjectNextProps {
  allRecordsCount: number
  allRecords: ProjectRecord[]
  currentRecordSlug: string
}

const props = defineProps<ProjectNextProps>()

const currentRecordIndex = props.allRecords.findIndex(
  (record: ProjectRecord) => record.slug === props.currentRecordSlug
)

const nextRecord: ProjectRecord =
  currentRecordIndex === props.allRecordsCount - 1
    ? props.allRecords[0]
    : props.allRecords[currentRecordIndex + 1]

const { emitterEmit } = useEmitter()
const sectionRef = ref<HTMLElement | null>(null)
const imageRef = ref<HTMLElement | null>(null)
const { isOutside } = useMouseInElement(sectionRef)

let triggeredOnce = false

watch(isOutside, (outside) => {
  if (!outside && !triggeredOnce) {
    triggeredOnce = true
    emitterEmit('hotspotEnter', {
      label: 'Show next'
    })
  }
})

const textRef = ref<HTMLElement | null>(null)
const isMounted = ref(false)

let ctx: gsap.Context | null = null

onMounted(() => {
  isMounted.value = true
  gsap.registerPlugin(ScrollTrigger)

  if (sectionRef.value && textRef.value) {
    ctx = gsap.context(() => {
      gsap.fromTo(
        textRef.value,
        { y: sectionRef.value!.offsetHeight },
        {
          y: 20,
          scrollTrigger: {
            trigger: sectionRef.value,
            start: 'top bottom',
            end: () => `+=${sectionRef.value!.offsetHeight}`,
            scrub: true
          }
        }
      )

      ScrollTrigger.refresh()
    }, sectionRef.value)
  }
})

onBeforeUnmount(() => {
  emitterEmit('hotspotLeave')

  if (ctx) {
    ctx.revert()
  }
})
</script>
